import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { BarElement, CategoryScale, Chart as ChartJS, Legend, LinearScale, Title, Tooltip } from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { useLocation as useAppLocation } from '../../contexts/LocationContext';
import {
  fetchAffordableHousingAprCapacity,
  fetchAffordableHousingAprPipeline,
  fetchAffordableHousingAprPreservation,
  fetchAffordableHousingAprSummary,
  type AffordableHousingAprCapacityRow,
  type AffordableHousingAprPipelineRow,
  type AffordableHousingAprPreservationRow,
  type AffordableHousingAprSummaryRow,
} from '../../services/affordableHousingAprService';
import { ChartLayout, EquityContent, MethodologyContent, OverviewContent, RelevanceContent } from './ChartLayout';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const BAR_COLORS = ['#1e3a8a', '#2563eb', '#0ea5e9', '#0d9488', '#15803d', '#7c3aed'];

/** Income bands available in APR completion and preservation records. Capacity is not split by income in this dataset. */
const INCOME_BAND_ORDER = ['extremely_low', 'very_low', 'low', 'moderate', 'above_moderate'] as const;

const INCOME_BAND_LABEL: Record<string, string> = {
  extremely_low: 'Extremely low income',
  very_low: 'Very low income',
  low: 'Low income',
  moderate: 'Moderate income',
  above_moderate: 'Above moderate income',
};

type IncomeBreakdownResult = {
  rows: { key: string; label: string; value: number }[];
  source: 'preservation' | 'none';
  reportingYear: number;
  hasAnyUnits: boolean;
};

function sumPreservationByIncomeForYear(
  preservation: AffordableHousingAprPreservationRow[],
  year: number
): Map<string, number> {
  const sums = new Map<string, number>();
  for (const r of preservation) {
    if (r.year !== year) continue;
    if (r.units == null) continue;
    const u = Number(r.units);
    if (Number.isNaN(u)) continue;
    const k = (r.income_level || '').trim() || 'unknown';
    sums.set(k, (sums.get(k) ?? 0) + u);
  }
  return sums;
}


function mapToOrderedRows(map: Map<string, number>): { key: string; label: string; value: number }[] {
  const order = INCOME_BAND_ORDER as readonly string[];
  const used = new Set<string>();
  const rows: { key: string; label: string; value: number }[] = [];
  for (const key of order) {
    const value = Math.round(map.get(key) ?? 0);
    rows.push({ key, label: INCOME_BAND_LABEL[key] ?? key, value });
    used.add(key);
  }
  for (const [key, v] of Array.from(map.entries())) {
    if (used.has(key)) continue;
    rows.push({ key, label: INCOME_BAND_LABEL[key] ?? key, value: Math.round(v) });
  }
  return rows;
}

function totalUnitsInMap(map: Map<string, number>): number {
  return Array.from(map.values()).reduce((acc, v) => acc + v, 0);
}

function buildIncomeBreakdownForPreservationYear(
  preservationYear: number,
  preservation: AffordableHousingAprPreservationRow[]
): IncomeBreakdownResult {
  const presMap = sumPreservationByIncomeForYear(preservation, preservationYear);
  if (totalUnitsInMap(presMap) > 0) {
    return {
      rows: mapToOrderedRows(presMap),
      source: 'preservation',
      reportingYear: preservationYear,
      hasAnyUnits: true,
    };
  }

  return {
    rows: mapToOrderedRows(new Map()),
    source: 'none',
    reportingYear: preservationYear,
    hasAnyUnits: false,
  };
}

function incomeSourceFootnote(source: IncomeBreakdownResult['source']): string {
  switch (source) {
    case 'preservation':
      return 'From preservation and rehabilitation activity, grouped by income.';
    default:
      return '';
  }
}

type AprProductionSegment = {
  key: string;
  label: string;
  value: number;
  year: number | null;
};

function orEmpty(result: { year: number; value: number } | null): { year: number | null; value: number } {
  if (result) return { year: result.year, value: result.value };
  return { year: null, value: 0 };
}

function latestNonNullSummaryField(
  rows: AffordableHousingAprSummaryRow[],
  field: 'total_proposed_units' | 'total_entitled_units' | 'total_permitted_units' | 'total_completed_units' | 'preserved_affordable_units'
): { year: number; value: number } | null {
  const sorted = [...rows].sort((a, b) => b.year - a.year);
  for (const r of sorted) {
    const raw = r[field];
    if (raw == null || Number.isNaN(Number(raw))) continue;
    return { year: r.year, value: Math.round(Number(raw)) };
  }
  return null;
}

function latestPipelineStageSum(
  pipeline: AffordableHousingAprPipelineRow[],
  stage: string
): { year: number; value: number } | null {
  const byYear = new Map<number, number>();
  for (const r of pipeline) {
    if (r.stage !== stage) continue;
    if (r.units == null) continue;
    const u = Number(r.units);
    if (Number.isNaN(u)) continue;
    byYear.set(r.year, (byYear.get(r.year) ?? 0) + u);
  }
  if (byYear.size === 0) return null;
  const years = Array.from(byYear.keys()).sort((a, b) => b - a);
  for (const y of years) {
    const v = Math.round(byYear.get(y) ?? 0);
    if (v !== 0) return { year: y, value: v };
  }
  return null;
}

function latestPreservationTableSum(
  preservation: AffordableHousingAprPreservationRow[]
): { year: number; value: number } | null {
  const byYear = new Map<number, number>();
  for (const r of preservation) {
    if (r.units == null) continue;
    const u = Number(r.units);
    if (Number.isNaN(u)) continue;
    byYear.set(r.year, (byYear.get(r.year) ?? 0) + u);
  }
  if (byYear.size === 0) return null;
  const years = Array.from(byYear.keys()).sort((a, b) => b - a);
  for (const y of years) {
    const v = Math.round(byYear.get(y) ?? 0);
    if (v !== 0) return { year: y, value: v };
  }
  return null;
}

function latestCapacitySum(capacity: AffordableHousingAprCapacityRow[]): { year: number; value: number } | null {
  const byYear = new Map<number, number>();
  for (const r of capacity) {
    if (r.realistic_capacity == null) continue;
    const n = Number(r.realistic_capacity);
    if (Number.isNaN(n)) continue;
    byYear.set(r.year, (byYear.get(r.year) ?? 0) + n);
  }
  const years = Array.from(byYear.keys()).sort((a, b) => b - a);
  for (const y of years) {
    const v = Math.round(byYear.get(y) ?? 0);
    if (v > 0) return { year: y, value: v };
  }
  return null;
}

function buildProductionSegments(
  summary: AffordableHousingAprSummaryRow[],
  pipeline: AffordableHousingAprPipelineRow[],
  preservation: AffordableHousingAprPreservationRow[],
  capacity: AffordableHousingAprCapacityRow[]
): AprProductionSegment[] {
  const cap = orEmpty(latestCapacitySum(capacity));
  const proposed = orEmpty(
    latestNonNullSummaryField(summary, 'total_proposed_units') ?? latestPipelineStageSum(pipeline, 'application')
  );
  const entitled = orEmpty(
    latestNonNullSummaryField(summary, 'total_entitled_units') ?? latestPipelineStageSum(pipeline, 'entitlement')
  );
  const permitted = orEmpty(
    latestNonNullSummaryField(summary, 'total_permitted_units') ?? latestPipelineStageSum(pipeline, 'building_permit')
  );
  const completed = orEmpty(
    latestNonNullSummaryField(summary, 'total_completed_units') ??
      latestPipelineStageSum(pipeline, 'certificate_of_occupancy')
  );
  const preserved = orEmpty(
    latestNonNullSummaryField(summary, 'preserved_affordable_units') ?? latestPreservationTableSum(preservation)
  );

  return [
    { key: 'capacity', label: 'Site capacity (realistic)', ...cap },
    { key: 'proposed', label: 'Proposed / in review', ...proposed },
    { key: 'entitled', label: 'Entitled', ...entitled },
    { key: 'permitted', label: 'Permitted', ...permitted },
    { key: 'completed', label: 'Completed (all units)', ...completed },
    { key: 'preservation', label: 'Preserved / rehab (affordable)', ...preserved },
  ];
}

function pickJurisdictionLabel(
  summary: AffordableHousingAprSummaryRow[],
  selectedLocation: string | null | undefined
): string {
  const name = summary[0]?.location_name?.trim();
  if (name) return name;
  const loc = selectedLocation?.trim();
  if (loc) return loc;
  return 'this jurisdiction';
}

export const AffordableHousingAprSection: React.FC = () => {
  const { selectedGeofips, selectedLocation } = useAppLocation();
  const [narrativeTab, setNarrativeTab] = useState('overview');
  const [summary, setSummary] = useState<AffordableHousingAprSummaryRow[]>([]);
  const [pipeline, setPipeline] = useState<AffordableHousingAprPipelineRow[]>([]);
  const [preservation, setPreservation] = useState<AffordableHousingAprPreservationRow[]>([]);
  const [capacity, setCapacity] = useState<AffordableHousingAprCapacityRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (selectedGeofips == null) {
      setSummary([]);
      setPipeline([]);
      setPreservation([]);
      setCapacity([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const [s, p, r, c] = await Promise.all([
        fetchAffordableHousingAprSummary(selectedGeofips, selectedLocation),
        fetchAffordableHousingAprPipeline(selectedGeofips, selectedLocation),
        fetchAffordableHousingAprPreservation(selectedGeofips, selectedLocation),
        fetchAffordableHousingAprCapacity(selectedGeofips, selectedLocation),
      ]);
      setSummary(s);
      setPipeline(p);
      setPreservation(r);
      setCapacity(c);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setError(
        msg.includes('relation') || msg.includes('does not exist')
          ? 'Affordable housing progress data from California HCD is not available in this deployment yet.'
          : msg
      );
      setSummary([]);
      setPipeline([]);
      setPreservation([]);
      setCapacity([]);
    } finally {
      setLoading(false);
    }
  }, [selectedGeofips, selectedLocation]);

  useEffect(() => {
    load();
  }, [load]);

  const segments = useMemo(
    () => buildProductionSegments(summary, pipeline, preservation, capacity),
    [summary, pipeline, preservation, capacity]
  );

  const hasAnySourceRows = useMemo(
    () => summary.length > 0 || pipeline.length > 0 || preservation.length > 0 || capacity.length > 0,
    [summary.length, pipeline.length, preservation.length, capacity.length]
  );

  /** True if at least one category has a reporting year or non-zero units (otherwise show message instead of blank bars). */
  const hasAnyCategoryData = useMemo(
    () => segments.some((s) => s.year != null || s.value > 0),
    [segments]
  );

  const jurisdictionLabel = useMemo(
    () => pickJurisdictionLabel(summary, selectedLocation),
    [summary, selectedLocation]
  );

  const isUnincorporated = useMemo(
    () => summary.some((r) => r.is_unincorporated_county_record),
    [summary]
  );

  const barChartData = useMemo(() => {
    return {
      labels: segments.map((s) => s.label),
      datasets: [
        {
          label: 'Units (latest reporting year per category)',
          data: segments.map((s) => s.value),
          backgroundColor: segments.map((s, i) =>
            s.year != null ? BAR_COLORS[i % BAR_COLORS.length] : '#e2e8f0'
          ),
        },
      ],
    };
  }, [segments]);

  const preservationSegment = useMemo(() => segments.find((s) => s.key === 'preservation'), [segments]);

  const preservationIncomeBreakdown = useMemo(() => {
    if (!preservationSegment || preservationSegment.value <= 0 || preservationSegment.year == null) return null;
    return buildIncomeBreakdownForPreservationYear(preservationSegment.year, preservation);
  }, [preservationSegment, preservation]);

  const preservationIncomeBarChartData = useMemo(() => {
    if (!preservationIncomeBreakdown?.hasAnyUnits) return null;
    return {
      labels: preservationIncomeBreakdown.rows.map((r) => r.label),
      datasets: [
        {
          label: `Preservation units by income (${preservationIncomeBreakdown.reportingYear})`,
          data: preservationIncomeBreakdown.rows.map((r) => r.value),
          backgroundColor: preservationIncomeBreakdown.rows.map((_, i) => BAR_COLORS[(i + 2) % BAR_COLORS.length]),
        },
      ],
    };
  }, [preservationIncomeBreakdown]);

  const preservationIncomeChartOptions = useMemo(() => {
    if (!preservationIncomeBreakdown) return null;
    const year = preservationIncomeBreakdown.reportingYear;
    return {
      indexAxis: 'y' as const,
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        x: {
          beginAtZero: true,
          title: { display: true, text: 'Units' },
          ticks: { font: { size: 11 } },
        },
        y: {
          ticks: {
            autoSkip: false,
            font: { size: 12 },
          },
          grid: { display: false },
        },
      },
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            label: (ctx: { parsed: { x?: number; y?: number } }) => {
              const v =
                typeof ctx.parsed.x === 'number'
                  ? ctx.parsed.x
                  : typeof ctx.parsed.y === 'number'
                    ? ctx.parsed.y
                    : 0;
              return ` ${typeof v === 'number' ? v.toLocaleString() : v} units`;
            },
            afterLabel: () => `Reporting year: ${year}`,
          },
        },
      },
    };
  }, [preservationIncomeBreakdown]);

  const overviewNarrative = useMemo(() => {
    if (!selectedGeofips) {
      return (
        <p>
          Select a location that matches HCD Annual Progress Report (APR) geography in ClimatePlans to see self-reported affordable
          housing production, pipeline, capacity, and preservation.
        </p>
      );
    }
    if (loading || error) return null;

    const reportedYears = segments.map((s) => s.year).filter((y): y is number => y != null);
    const yearSpan =
      reportedYears.length === 0
        ? 'none loaded'
        : new Set(reportedYears).size === 1
          ? `${reportedYears[0]}`
          : `${Math.min(...reportedYears)}–${Math.max(...reportedYears)}`;

    const noRowsWarning = !hasAnySourceRows ? (
      <p>
        ClimatePlans does not have California HCD Annual Progress Report rows for <strong>{jurisdictionLabel}</strong> yet. The place may
        not appear in the current data release, the name may not match HCD’s jurisdiction list, or progress data may not be loaded for
        this site. When no values exist, the chart area explains that instead of showing empty bars.
      </p>
    ) : null;

    return (
      <>
        {noRowsWarning}
        {hasAnyCategoryData ? (
          <p>
            <strong>{jurisdictionLabel}</strong>. Reporting years in ClimatePlans for this location: <strong>{yearSpan}</strong>.
          </p>
        ) : (
          <p>
            For <strong>{jurisdictionLabel}</strong>, none of the APR production categories in this release have a reporting year or
            non-zero units, so the chart area shows a short notice instead of empty bars. Try another city or county, or check back after
            data updates.
          </p>
        )}
        <p style={{ lineHeight: 1.65 }}>
          {segments.map((s, i) => (
            <span key={s.key}>
              {i > 0 ? ' · ' : null}
              <strong>{s.label}</strong>: {s.value.toLocaleString()} units
              {s.year != null ? ` (${s.year})` : ' (no data)'}
            </span>
          ))}
          .
        </p>
        {hasAnyCategoryData ? (
          <p style={{ fontSize: '0.88rem', color: '#64748b', marginTop: '0.75rem' }}>
            Categories at zero with no year had no values in the HCD progress data used here for that measure (including site capacity from
            housing element site inventories where provided).
          </p>
        ) : null}
        {isUnincorporated ? (
          <p style={{ fontSize: '0.9rem', color: '#64748b' }}>
            At least one loaded record is an <strong>unincorporated county</strong> jurisdiction (not the full county).
          </p>
        ) : null}
      </>
    );
  }, [selectedGeofips, loading, error, segments, jurisdictionLabel, isUnincorporated, hasAnySourceRows, hasAnyCategoryData]);

  const overviewDescription = useMemo(() => {
    const intro =
      'Self-reported affordable housing unit counts from California Housing and Community Development (HCD) Annual Progress Reports (APR).';
    const capacityText =
      'Capacity refers to the amount of housing a jurisdiction says could be built based on available land and zoning. It comes from identified parcels in the general plan housing element where cities or counties estimate realistic capacity, meaning how many units those sites could accommodate. It reflects planned or potential development, not actual construction.';
    const preservationText =
      'Preservation refers to actions that maintain existing affordable housing rather than building new units. This includes rehabilitating units, preserving at-risk affordable housing, acquiring properties to keep them affordable, and protecting mobilehome park units. It represents affordability that is retained over time, not new supply.';

    const capacitySeg = segments.find((s) => s.key === 'capacity');
    const preservationSeg = segments.find((s) => s.key === 'preservation');
    const hasCapacityData = (capacitySeg?.value ?? 0) > 0;
    const hasPreservationData = (preservationSeg?.value ?? 0) > 0;

    if (hasCapacityData && hasPreservationData) {
      return `${intro} ${capacityText} ${preservationText}`;
    }
    if (hasCapacityData) {
      return `${intro} ${capacityText}`;
    }
    if (hasPreservationData) {
      return `${intro} ${preservationText}`;
    }
    return `${intro} Each bar uses the latest reporting year for that category when data exist. APR reflects permitting, certificates of occupancy, preservation, and broader housing element progress, which bears on land use, displacement risk, and transportation demand.`;
  }, [segments]);

  const chartOptions = useMemo(
    () => ({
      indexAxis: 'y' as const,
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        x: {
          beginAtZero: true,
          title: { display: true, text: 'Units' },
          ticks: { font: { size: 11 } },
        },
        y: {
          ticks: {
            autoSkip: false,
            font: { size: 12 },
          },
          grid: { display: false },
        },
      },
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            label: (ctx: { parsed: { x?: number; y?: number }; dataIndex: number }) => {
              const v =
                typeof ctx.parsed.x === 'number'
                  ? ctx.parsed.x
                  : typeof ctx.parsed.y === 'number'
                    ? ctx.parsed.y
                    : 0;
              return ` ${typeof v === 'number' ? v.toLocaleString() : v} units`;
            },
            afterLabel: (ctx: { dataIndex: number }) => {
              const seg = segments[ctx.dataIndex];
              if (!seg) return '';
              return seg.year != null
                ? `Reporting year: ${seg.year}`
                : 'No data for this category for this location';
            },
          },
        },
      },
    }),
    [segments]
  );

  const chartContent = (() => {
    if (selectedGeofips == null) {
      return (
        <div style={{ padding: '1.25rem', color: '#64748b', lineHeight: 1.6 }}>
          Select a location to view affordable housing production from California HCD APR data.
        </div>
      );
    }
    if (loading) {
      return <div style={{ padding: '2rem', color: '#64748b' }}>Loading affordable housing data…</div>;
    }
    if (error) {
      return <div style={{ padding: '1rem', color: '#b91c1c' }}>{error}</div>;
    }
    if (!hasAnyCategoryData) {
      return (
        <div
          style={{
            padding: '1.5rem 1.25rem',
            color: '#475569',
            lineHeight: 1.65,
            border: '1px dashed #cbd5e1',
            borderRadius: 8,
            background: '#f8fafc',
          }}
        >
          <strong>No APR unit counts for this location.</strong>
          <p style={{ margin: '0.75rem 0 0', fontSize: '0.95rem' }}>
            None of the six production categories (site capacity, proposed, entitled, permitted, completed, or preservation) have values in
            ClimatePlans for <strong>{jurisdictionLabel}</strong> in this release. Try another city or county, or check back after data
            updates.
          </p>
        </div>
      );
    }
    return (
      <>
        <div
          style={{ minHeight: 340, height: Math.max(340, segments.length * 52), position: 'relative' }}
          id="affordable-housing-apr-chart"
        >
          <Bar data={barChartData} options={chartOptions} />
          <p style={{ fontSize: '0.82rem', color: '#64748b', marginTop: '0.5rem', marginBottom: 0 }}>
            Reporting years can differ across categories.
          </p>
        </div>

        {preservationSegment && preservationSegment.value > 0 && preservationIncomeBreakdown?.hasAnyUnits ? (
          <div style={{ marginTop: '2rem' }}>
            <h4 style={{ fontSize: '1.05rem', fontWeight: 600, margin: '0 0 0.35rem', color: '#0f172a' }}>Preservation units by income category</h4>
            <p style={{ fontSize: '0.82rem', color: '#64748b', marginBottom: '0.65rem', lineHeight: 1.55 }}>
              Preservation is shown by income band when those category values are available. This breakout uses the same reporting year as
              the preservation total in the main chart (<strong>{preservationSegment.year}</strong>).
            </p>
            {preservationIncomeBarChartData && preservationIncomeChartOptions ? (
              <>
                <div
                  style={{
                    minHeight: 280,
                    height: Math.max(280, preservationIncomeBreakdown.rows.length * 48),
                    position: 'relative',
                  }}
                  id="affordable-housing-apr-preservation-income-chart"
                >
                  <Bar data={preservationIncomeBarChartData} options={preservationIncomeChartOptions} />
                </div>
                <p style={{ fontSize: '0.8rem', color: '#64748b', marginTop: '0.45rem', marginBottom: 0 }}>
                  {incomeSourceFootnote(preservationIncomeBreakdown.source)}
                </p>
              </>
            ) : null}
          </div>
        ) : null}
      </>
    );
  })();

  return (
    <ChartLayout
      title="Affordable Housing Production"
      chartType="bar"
      activeView={narrativeTab}
      onViewChange={setNarrativeTab}
      chartContent={chartContent}
      overviewChartId="affordable_housing_production_latest"
      overviewContent={
        <OverviewContent
          title="Overview"
          description={overviewDescription}
        >
          {overviewNarrative || <p>Loading overview…</p>}
        </OverviewContent>
      }
      relevanceContent={
        <RelevanceContent
          title="Relevance to climate action"
          description="Where housing is built and whether it is affordable shapes travel distances, mode choice, and building energy demand."
        >
          <div className="methodology-details-simple">
            <p>
              Infill and multifamily housing near jobs and transit can reduce per-capita VMT and emissions. When affordable housing is far
              from employment centers, lower-income households often face longer commutes and higher transport costs. Preservation limits
              displacement and supports climate equity.
            </p>
          </div>
        </RelevanceContent>
      }
      equityContent={
        <EquityContent title="Equity" description="Affordable housing outcomes are core equity indicators for climate planning.">
          <div className="methodology-details-simple">
            <p>
              Very-low- and low-income households face the greatest housing cost burdens and displacement risk. Deed-restricted units help
              lock in affordability over time. Preservation and mobilehome park protection can matter as much as new construction for
              stabilizing communities.
            </p>
          </div>
        </EquityContent>
      }
      methodologyContent={
        <MethodologyContent
          title="Methodology"
          description="Data are self-reported by cities and counties to HCD and are not independently verified. County-name jurisdictions may represent unincorporated county areas only."
          dataSources={[
            'California Department of Housing and Community Development — Annual Progress Reports (https://www.hcd.ca.gov/planning-and-community-development/annual-progress-reports)',
            'ClimatePlans housing progress database (HCD APR–aligned tables)',
          ]}
          methodology={[
            'The selected place is matched to HCD jurisdiction records (including common name and ID formats).',
            'Each category is always shown. Values use the latest reporting year with data for that measure; missing categories are zero.',
            'Completed units prefer annual summary rollups; certificate-of-occupancy detail is used when summaries are missing.',
            'Site capacity uses the latest year with positive realistic capacity from housing element site inventories where HCD provides them.',
            'Preservation is broken out by income only when preservation totals are positive and HCD reports category-level preservation values for that same year.',
            'Site capacity is not broken out by income because the HCD capacity data loaded here do not include income-category units.',
          ]}
        />
      }
      showRelevanceEquity={true}
      enableComments={true}
      location={selectedLocation || 'This area'}
      appendLocationToTitle={true}
    />
  );
};

export default AffordableHousingAprSection;
